import { useState, useEffect } from "react";
import { Check, Gift } from "lucide-react";
import axios from "axios";
import { API } from "../components/app/constants";

export const ShareReferralBox = ({ user, onUserUpdate }) => {
  const [referralCode, setReferralCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [firstShareClaimed, setFirstShareClaimed] = useState(false);
  const [claimingBonus, setClaimingBonus] = useState(false);
  
  useEffect(() => {
    if (user) {
      axios.get(`${API}/shares/${user.id}`)
        .then(res => {
          setReferralCode(res.data.referral_code);
          setFirstShareClaimed(res.data.first_share_claimed);
        })
        .catch(() => {});
    }
  }, [user]);
  
  const copyReferralLink = () => {
    const link = `${window.location.origin}/start?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const shareOnSocialAndClaim = async (platform) => {
    const shareText = "Just created an amazing AI portrait with StarFrame AI! Transform your photos into stunning professional portraits:";
    const shareUrl = `https://starframeai.com/start?ref=${referralCode}`;
    const fullText = `${shareText} ${shareUrl}`;
    
    let url = "";
    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(fullText)}`;
        break;
      case "viber":
        url = `viber://forward?text=${encodeURIComponent(fullText)}`;
        break;
      case "telegram":
        url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
        break;
      default:
        return;
    }
    
    // Open social share window FIRST
    window.open(url, "_blank", "width=600,height=400");
    
    // Then claim bonus (only if not already claimed)
    if (!firstShareClaimed && !claimingBonus) {
      setClaimingBonus(true);
      try {
        const response = await axios.post(`${API}/share/first-time`, { user_id: user.id, platform });
        if (response.data.success) {
          setFirstShareClaimed(true);
          onUserUpdate({ ...user, credits: response.data.new_balance });
        }
      } catch {
        // non-critical
      }
      setClaimingBonus(false);
    }
  };
  
  return (
    <div className="glass-card p-6 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Gift className="w-5 h-5 text-[#D4AF37]" />
        <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Earn More Credits</span>
      </div>
      
      {/* Share on Social to Earn Credits */}
      {!firstShareClaimed && (
        <div className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl">
          <p className="text-sm mb-3">Share StarFrame AI on social media and earn <span className="text-[#D4AF37] font-bold">3 free credits</span>!</p>
          <div className="grid grid-cols-3 gap-2">
            <button 
              onClick={() => shareOnSocialAndClaim("twitter")}
              disabled={claimingBonus}
              className="bg-[#1DA1F2]/20 hover:bg-[#1DA1F2]/30 text-[#1DA1F2] py-2 rounded-lg text-xs font-medium transition-colors"
              data-testid="share-earn-twitter"
            >
              Twitter / X
            </button>
            <button 
              onClick={() => shareOnSocialAndClaim("linkedin")}
              disabled={claimingBonus}
              className="bg-[#0A66C2]/20 hover:bg-[#0A66C2]/30 text-[#0A66C2] py-2 rounded-lg text-xs font-medium transition-colors"
              data-testid="share-earn-linkedin"
            >
              LinkedIn
            </button>
            <button 
              onClick={() => shareOnSocialAndClaim("facebook")}
              disabled={claimingBonus}
              className="bg-[#1877F2]/20 hover:bg-[#1877F2]/30 text-[#1877F2] py-2 rounded-lg text-xs font-medium transition-colors"
              data-testid="share-earn-facebook"
            >
              Facebook
            </button>
            <button 
              onClick={() => shareOnSocialAndClaim("whatsapp")}
              disabled={claimingBonus}
              className="bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] py-2 rounded-lg text-xs font-medium transition-colors"
              data-testid="share-earn-whatsapp"
            >
              WhatsApp
            </button>
            <button 
              onClick={() => shareOnSocialAndClaim("viber")}
              disabled={claimingBonus}
              className="bg-[#7360F2]/20 hover:bg-[#7360F2]/30 text-[#7360F2] py-2 rounded-lg text-xs font-medium transition-colors"
              data-testid="share-earn-viber"
            >
              Viber
            </button>
            <button 
              onClick={() => shareOnSocialAndClaim("telegram")}
              disabled={claimingBonus}
              className="bg-[#26A5E4]/20 hover:bg-[#26A5E4]/30 text-[#26A5E4] py-2 rounded-lg text-xs font-medium transition-colors"
              data-testid="share-earn-telegram"
            >
              Telegram
            </button>
          </div>
        </div>
      )}
      
      {firstShareClaimed && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center gap-2">
          <Check className="w-4 h-4 text-green-400" />
          <p className="text-green-300 text-sm">Share bonus claimed! You earned 3 credits.</p>
        </div>
      )}
      
      {/* Referral Link */}
      <div>
        <p className="text-sm text-white/60 mb-2">
          Invite friends with your link. You earn <span className="text-[#D4AF37] font-bold">3 credits</span> for each friend who joins!
        </p>
        <div className="flex gap-2">
          <input 
            type="text" 
            readOnly 
            value={`${window.location.origin}/start?ref=${referralCode}`}
            className="input-luxury flex-1 text-sm py-2"
            data-testid="referral-link"
          />
          <button 
            onClick={copyReferralLink}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${copied ? 'bg-[#D4AF37] text-black' : 'bg-white/10 hover:bg-white/20'}`}
            data-testid="copy-referral"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>
    </div>
  );
};

